import { useHandleApi } from 'hooks';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useBrandStore } from 'stores';
import type { PersonaFileData } from './BrandPersonasForm.types';
import BrandPersonasFormView from './BrandPersonasForm.View';

const BrandPersonasForm: React.FC = () => {
  const apiDomain = import.meta.env.VITE_PLATFORM_API_URL;
  const { brand } = useBrandStore();
  const { loading, handleApi } = useHandleApi({ apiDomain });
  const [fileData, setFileData] = useState<PersonaFileData | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [personas, setPersonas] = useState<{ data: unknown }>({ data: [] });
  const [personaCrossBrandPurchases, setPersonaCrossBrandPurchases] = useState<unknown[]>([]);

  const handleFileSelect = (file: File) => {
    if (file.type !== 'application/json') {
      setError('Please select a JSON file only.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      setError('File size must be less than 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = JSON.parse(e.target?.result as string);

        setFileData({
          name: file.name,
          size: file.size,
          content
        });
        setError(null);
        setSuccess(null);
      } catch {
        setError('Invalid JSON file. Please check the file format.');
        setFileData(null);
      }
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const { files } = e.dataTransfer;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const fetchPersonas = async () => {
    if (!brand?.brandId) return;

    try {
      const [personaResponse, personaCrossBrandResponse] = await Promise.all([
        handleApi({
          endpoint: `/brand/${brand.brandId}/persona`,
          method: 'GET',
          skipErrorToaster: true
        }),
        handleApi({
          endpoint: `/brand/${brand.brandId}/personas-cross-brand-purchases`,
          method: 'GET',
          skipErrorToaster: true
        })
      ]);

      // Only set data if the response is successful (status 200-299)
      if (
        personaResponse.status &&
        personaResponse.status >= 200 &&
        personaResponse.status < 300 &&
        personaResponse.data
      ) {
        setPersonas(personaResponse.data);
      } else {
        setPersonas({ data: [] });
      }

      if (
        personaCrossBrandResponse.status &&
        personaCrossBrandResponse.status >= 200 &&
        personaCrossBrandResponse.status < 300 &&
        personaCrossBrandResponse.data
      ) {
        setPersonaCrossBrandPurchases(personaCrossBrandResponse.data);
      } else {
        setPersonaCrossBrandPurchases([]);
      }
    } catch {
      // Handle any errors gracefully - set empty arrays instead of crashing
      setPersonas({ data: [] });
      setPersonaCrossBrandPurchases([]);
    }
  };

  const submitPersonas = async () => {
    if (!fileData || !brand?.brandId) {
      setError('Please select a file and ensure brand is available.');
      return;
    }
    const { brand_id: formBrandId, other_brands_analysis: otherBrandsAnalysis } = fileData.content;

    if (!formBrandId) {
      setError('The JSON file is missing required field brand_id.');
      return;
    }
    if (formBrandId !== brand.brandId) {
      setError("brand_id in the file does not match the current Brand's.");
      return;
    }

    const apiCalls = [
      handleApi({
        endpoint: `/brand/${brand.brandId}/persona`,
        method: 'PUT',
        data: {
          data: fileData.content
        },
        skipErrorToaster: false
      })
    ];

    if (otherBrandsAnalysis) {
      apiCalls.push(
        handleApi({
          endpoint: `/brand/${brand.brandId}/personas-cross-brand-purchases`,
          method: 'PUT',
          data: {
            data: otherBrandsAnalysis,
            purchaseType: 'others_products'
          }
        }),
        handleApi({
          endpoint: `/brand/${brand.brandId}/personas-cross-brand-purchases`,
          method: 'PUT',
          data: {
            data: otherBrandsAnalysis,
            purchaseType: 'top_brands'
          },
          skipErrorToaster: false
        })
      );
    }

    const responses = await Promise.all(apiCalls);

    if (responses.every(response => response.status === 200)) {
      setSuccess(`Personas uploaded successfully for brand: ${brand.brandId}`);
      setFileData(null);
      setError(null);
      fetchPersonas();
    } else {
      setError('Failed to upload personas. Please try again.');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    fetchPersonas();
  }, [brand?.brandId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (fileData) {
      submitPersonas();
    }
  }, [fileData]);

  return (
    <BrandPersonasFormView
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      handleFileInputChange={handleFileInputChange}
      dragOver={dragOver}
      error={error}
      fileData={fileData}
      success={success}
      loading={loading}
      personas={personas}
      personaCrossBrandPurchases={personaCrossBrandPurchases}
      brand={brand}
    />
  );
};

export default BrandPersonasForm;
